import { Component, ElementRef, OnInit, ViewChild, signal, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from "underscore";

import * as Sentry from "@sentry/angular";

import {
  faMagnifyingGlassChart,
  faPrint,
  faBook,
  faCircleCheck,
  faCircleXmark,
  faQuestionCircle,
  faFileExcel,
  faSpinner,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import {faMinusSquare, faPlusSquare} from '@fortawesome/free-regular-svg-icons' ;

import { ManagementUser } from '../../../../core/models/management-user.model';
import { District } from '../../../../core/models/district.model';
import { School } from '../../../../core/models/school.model';
import { SubscriptionTypes } from '../../../../core/models/subscription-types.model';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import { Student } from '../../../../core/models/student.model';
import { Unit } from '../../../../core/models/unit.model';
import { InterventionTaskStatus } from '../../../../core/models/intervention-task-status.model';
import { ReportsService } from "../../../../core/services/reports.service";
import { PrintService } from 'src/app/core/services/print.service';
import { TaskCategories, TaskCategory, TaskCategoryTypes } from '../../../../core/models/task-category.model';
import { LoadingService } from '../../../../core/services/loading.service';

import { unitDetailIntroSteps } from './unit-detail.intro-steps';
import { canViewCoTeacherFilter, canViewSchoolFilter } from 'src/app/reports/utils';
import { SortColumn, sortUpdate } from 'src/app/core/utils/table';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

type SortableTaskColumn =
  | 'dateCompleted'
  | 'duration'
  | 'taskName'
  | 'wordListString'
  | 'unitNumber'
  | 'level'
  | 'percentCorrect'
  | undefined ;

type TaskFilters = {
  unit?: Unit
  category: TaskCategory
}

@Component({
    selector: 'wf-unit-detail',
    templateUrl: './unit-detail.component.html',
    styleUrls: ['./unit-detail.component.css'],
    standalone: false
})
export class UnitDetailComponent implements OnInit {
  TaskCategoryTypes = TaskCategoryTypes;
  currentUser: ManagementUser | null = null ;
  taskRetrievalError: boolean = false ;
  showSchoolFilter: boolean = false ;
  readonly hideSharedStudents = signal(false);
  showCoTeacherFilter: boolean = false ;
  schoolFilterError: string = '' ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  students: Student[] = [] ;
  unfilteredStudents: Student[] = [] ;
  currentStudents: Student[] = [] ;
  selectedStudent: Student | undefined ;
  selectedUnit?: Unit;
  units: Unit[] = [] ;
  taskCategories: Array<TaskCategory> = TaskCategories ;
  defaultTaskCategory: TaskCategory = TaskCategories[0] ;
  selectedTaskCategory: TaskCategory = this.defaultTaskCategory ;
  fullProduct: string = SubscriptionTypes.FullProduct ;
  reportsIcon = CATEGORY_ICONS['class-reports'] ;
  detailIcon = faMagnifyingGlassChart ;
  printIcon = faPrint ;
  titleIcon = faBook ;
  correctIcon = faCircleCheck ;
  incorrectIcon = faCircleXmark ;
  expandIcon = faPlusSquare ;
  collapseIcon = faMinusSquare ;
  expandedTaskId: number = -1 ;
  helpIcon = faQuestionCircle;
  excelIcon = faFileExcel;
  loadingIcon = faSpinner;
  errorIcon = faCircleExclamation;
  exportingReport: boolean = false;
  reportError: string = '';
  tourSteps = unitDetailIntroSteps;

  private readonly studentTasks = signal<InterventionTaskStatus[]>([]);
  readonly studentTaskFilters = signal<TaskFilters>({
    category: this.selectedTaskCategory
  });
  readonly studentTaskSort = signal<SortColumn>({ reverse: false });

  readonly studentTasksDisplay = computed(() => {
    const filteredTasks = this.filterTasks(this.studentTasks(), this.studentTaskFilters());
    return this.sortTasks(filteredTasks, this.studentTaskSort());
  });

  private filterTasks(tasks: InterventionTaskStatus[], filters: TaskFilters) {
    return tasks.filter(task =>
      task.unitNumber === filters.unit?.number && (filters.category.type === TaskCategoryTypes.ALL || (task.taskCategories?.includes(filters.category.type)))
    )
  }

  private sortTasks(tasks: InterventionTaskStatus[], sort: SortColumn) {

    const sortOn = (task: InterventionTaskStatus) => {
      if (sort.column === 'percentCorrect') {
        return task.numberOfTrials === 0 ? 0 : task.correctTrials / task.numberOfTrials
      }
      if (sort.column === 'duration') {
        const [hr, min] = task.duration.split(":").map(n => parseInt(n, 10));
        return (hr * 60) + min;
      }

      return sort.column ? (task as any)[sort.column] : 0;
    }

    const sorted = _.sortBy(tasks, sortOn);
    return sort.reverse ? sorted.reverse() : sorted;
  }


  @ViewChild('printContent') printContent!: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private sessionStorageService: SessionStorageService,
    private printService: PrintService,
    private loadingService: LoadingService,
  ) { }

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    if (this.route.snapshot.data['resolveData'].erred)
    {
      this.schoolFilterError = this.route.snapshot.data['resolveData'].message ;
    }
    else
    {
      this.students = this.filterCoAccessStudents(this.route.snapshot.data['resolveData']) ;
      this.unfilteredStudents = this.filterCoAccessStudents(this.students) ;

      this.currentStudents = this.students ;
      if (this.students && this.students.length > 0) {
        this.selectedStudent = this.students[0];
        this.getInterventionTaskDetails();
      }
    }

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }

    if (this.showCoTeacherFilter) {
      this.checkForSharedStudents() ;
    }
  }

  private checkForSharedStudents() {
    if (this.showCoTeacherFilter) {
      const hasAnySharedStudents = this.students.some(student => student.teacherID !== this.reportsService.getSelectedTeacherForReports().userID) ;
      this.showCoTeacherFilter = hasAnySharedStudents;
    }
    else {
      this.showCoTeacherFilter = false;
    }
  }

  filterSchoolTeacher() {
    let teacherId = this.reportsService.getSelectedTeacherForReports().userID ;

    // We need to make sure we have a teacher selected
    if (teacherId === 0)
    {
      this.schoolFilterError = 'Please select a Teacher' ;

      return ;
    }

    this.reportsService.getStudentsForTeacher(teacherId).subscribe({
      next: (students) => {
        this.currentStudents = this.filterCoAccessStudents(students) ;
        this.selectedStudent = undefined ;
        this.studentTasks.set([]);
        this.schoolFilterError = '' ;

        if (this.currentStudents && this.currentStudents.length > 0) {
          this.selectedStudent = this.currentStudents[0];
          this.getInterventionTaskDetails();
        }
      },
      error: (error) => {
        this.schoolFilterError = error ;
      }
    })
  }

  studentTaskFilterChanged() {
    this.studentTaskFilters.set({
      unit: this.selectedUnit,
      category: this.selectedTaskCategory
    })
  }

  filterCoAccessStudents(students: Student[]) {
    const currentUser = this.sessionStorageService.userData() ;
    return currentUser?.isTeacherUser() ? students : students.filter(student => student.teacherID === this.reportsService.getSelectedTeacherForReports().userID) ;
  }

  getInterventionTaskDetails() {
    // When a student is selected, we retrieve the unit details for that student
    let schoolId = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let teacherId = this.reportsService.getSelectedTeacherForReports().userID ;

    this.schoolFilterError = '' ;
    this.studentTasks.set([]);
    // this.filteredStudentTasks = [] ;
    this.taskRetrievalError = false ;
    this.loadingService.start() ;
    this.reportsService.getUnitDetailReportData(schoolId, teacherId, this.selectedStudent!.userID).subscribe({
      next: (tasks) => {
        this.loadingService.finish() ;
        this.studentTasks.set(tasks);

        // Now, create our units from the returned tasks. To do this, we need to
        // go through all our task details and get the unit it was associated with,
        // building up a unique list of unit info
        this.units = [] ;
        tasks.forEach((task) => {
          // Adjust some of our properties for these tasks
          if (!task.taskName.includes('Masked') && !task.taskID.startsWith('CTM') && (task.taskID.charAt(task.taskID.length - 2) === 'M' || task.taskID.charAt(task.taskID.length - 3) === 'M'))
          {
            task.taskName += ' Masked' ;
          }

          if (task.taskID.startsWith("PASS") ||
              task.taskID.startsWith("PAP") ||
              task.taskID.startsWith("PAQ") ||
              task.taskID.startsWith("FTP") ||
              task.taskID.startsWith("MTP"))
          {
            task.wordListString = 'Single Wordlist' ;
          }

          let addedBefore = this.units.filter((unit) => unit.number === task.unitNumber);
          if (!addedBefore.length)
          {
            this.units.push({
              number: task.unitNumber,
              summary: `${task.unitNumber} - ${task.unitDescription}`,
              description: task.unitDescription,
              dateCompleted: task.dateCompleted,
            }) ;
          }else{
            // item has already been added before so we need to update dateCompleted
            if(addedBefore[0].dateCompleted < task.dateCompleted){
              let indexBefore = this.units.indexOf(addedBefore[0]);
              this.units[indexBefore].dateCompleted = task.dateCompleted;
            }
          }
        }) ;

        // Sort units based on task completion date and set selected unit to first one
        if(this.units.length > 0){
          this.units.sort((task1, task2) => task1.dateCompleted > task2.dateCompleted ? -1 : task1.dateCompleted < task2.dateCompleted ? 1 : 0);
          this.selectedUnit = this.units[0]
          this.units.unshift({ number: 0, summary: 'All', description: 'All', dateCompleted: 0 });
          // Trigger filtering tasks to update values in the table initially
          this.studentTaskFilterChanged();
        }
      },
      error: (err: any) => {
        this.taskRetrievalError = true ;
        this.loadingService.finish() ;

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'unit-detail',
            schoolId: schoolId,
            teacherId: teacherId,
            studentId: (this.selectedStudent) ? this.selectedStudent.userID : 'no-student-selected',
          }
        }) ;
      }
    })
  }

  refreshUnitDetails(){
    if(this.selectedStudent !== undefined ){
      this.getInterventionTaskDetails() ;
    }
  }

  sortChanged(column: SortableTaskColumn) {
    if (column) {
      this.studentTaskSort.update(sortUpdate(column));
    }
  }

  toggleTaskTrials(taskId: number) {
    this.expandedTaskId = (taskId === this.expandedTaskId) ? -1 : taskId ;
  }

  trackByStudentId(index: number, obj: Student) {
    return obj.userID ;
  }

  toggleSharedStudents() {
    const selectedTeacherId = this.reportsService.getSelectedTeacherForReports().userID;
    this.currentStudents = this.hideSharedStudents()
      ? this.unfilteredStudents.filter(student => student.teacherID === selectedTeacherId)
      : this.students;

    // TODO: switch selectedStudent to linkedSignal when that is available
    this.selectedStudent = this.currentStudents[0];
    if (this.selectedStudent) {
      this.getInterventionTaskDetails();
    } else {
      this.studentTasks.set([]);
    }
  }

  exportUnitDetail(includeTrials: boolean) {
    if (!this.selectedStudent || !this.selectedUnit) return;

    this.reportError = '';
    this.exportingReport = true;

    const schoolId  = this.reportsService.getSelectedSchoolForReports().schoolID;
    const teacherId = this.reportsService.getSelectedTeacherForReports().userID;
    const studentId = this.selectedStudent.userID;
    const unitNumber = this.selectedUnit.number;
    const categoryType = this.selectedTaskCategory.type;
    const categoryName = this.selectedTaskCategory.name;

    this.reportsService.downloadUnitDetail(schoolId, teacherId, studentId, unitNumber, categoryType, categoryName, includeTrials)
      .subscribe({
        next: (blob: Blob) => {
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = 'unit_detail.xlsx';
          a.click();
          URL.revokeObjectURL(objectUrl);
          this.exportingReport = false;
        },
        error: (err: any) => {
          this.exportingReport = false;
          this.reportError = 'There was an error trying to export the Unit Detail report. Please contact support for assistance.';

          Sentry.captureException(err, {
            tags: {
              section: 'reports',
              report: 'unit-detail',
              action: 'export',
              schoolId: schoolId,
              teacherId: teacherId,
              studentId: studentId,
            }
          });
        }
      });
  }

  printSummary() {
    let school = this.reportsService.getSelectedSchoolForReports().name;
    let teacher = this.reportsService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }
}
